// Shared dd/mm/yyyy date input helpers for the web frontends (booking, webportal).
// Dates are exchanged as ISO calendar dates (YYYY-MM-DD) so they compare as strings
// and never shift because of timezones.

export const DATE_INPUT_LENGTH = 'DD/MM/YYYY'.length

// Year the picker scrolls to when no date has been chosen yet
export const DEFAULT_PICKER_YEAR = 2000

const DATE_INPUT_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/
const ISO_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})/

const pad = (value: number, length = 2) => String(value).padStart(length, '0')

export const toIsoDate = (date: Date): string =>
  `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

// Local midnight, so pickers show the same calendar day as the ISO date
export const isoDateToDate = (isoDate: string): Date | null => {
  const match = ISO_DATE_REGEX.exec(isoDate)
  if (!match) {
    return null
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

// Accepts any value starting with YYYY-MM-DD (also full ISO timestamps)
export const isoDateToDateInput = (isoDate?: string | null): string => {
  const match = isoDate ? ISO_DATE_REGEX.exec(isoDate) : null
  return match ? `${match[3]}/${match[2]}/${match[1]}` : ''
}

// Keeps only digits and inserts slashes so the text follows dd/mm/yyyy
export const maskDateInput = (text: string): string => {
  const digits = text.replace(/\D/g, '').slice(0, 8)
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
    .filter(Boolean)
    .join('/')
}

// Returns YYYY-MM-DD for a complete, existing calendar date within the (inclusive) bounds, otherwise null
export const parseDateInput = (
  text: string,
  { max, min }: { max?: string; min?: string } = {},
): string | null => {
  const match = DATE_INPUT_REGEX.exec(text)
  if (!match) {
    return null
  }

  const day = Number(match[1])
  const month = Number(match[2])
  const year = Number(match[3])
  // Date rolls overflowing values over (30/02 -> 02/03), so compare the parts after constructing it
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null
  }

  const isoDate = `${match[3]}-${match[2]}-${match[1]}`
  if ((min && isoDate < min.slice(0, 10)) || (max && isoDate > max.slice(0, 10))) {
    return null
  }
  return isoDate
}

export const getInitialPickerDate = ({
  max,
  min,
  value,
}: {
  max?: string
  min?: string
  value?: string | null
}): string => {
  if (value) {
    return value
  }
  const fallback = `${DEFAULT_PICKER_YEAR}-01-01`
  if (max && fallback > max) {
    return max
  }
  if (min && fallback < min) {
    return min
  }
  return fallback
}
